import { captureElement } from './elementCapture';

/**
 * Space around the copied content, in CSS pixels. Without it, borders and headings sit on
 * the image edge, and text that renders a pixel wider in the clone (embedded fonts) is
 * clipped at the right.
 */
export const CAPTURE_MARGIN = 16;

export interface RenderElementToPngOptions {
    /** Any valid CSS color. Defaults to the surface of the theme on screen. */
    backgroundColor?: string;
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob === null) {
                reject(new Error('Failed to render element as PNG'));
                return;
            }
            resolve(blob);
        }, 'image/png');
    });
}

/**
 * Rasterises a DOM subtree to a PNG blob through the shared capture in
 * `@/utils/elementCapture`, framed by {@link CAPTURE_MARGIN}.
 *
 * The image is opaque. A transparent one shows whatever the paste target puts behind it, so a
 * dark-theme section heading would vanish on a white page, and Office on Windows can composite
 * the alpha channel to black because it reads the alpha-less `CF_DIB` clipboard flavour
 * instead of the PNG.
 */
export async function renderElementToPngBlob(
    element: HTMLElement,
    options: RenderElementToPngOptions = {}
): Promise<Blob> {
    const canvas = await captureElement(element, {
        backgroundColor: options.backgroundColor,
        margin: CAPTURE_MARGIN,
    });
    return canvasToPngBlob(canvas);
}
